import { useState } from "react";
import { toast } from "sonner";
import { Landmark, Copy, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";

const fmtIban = (i) => (i || "").replace(/(.{4})/g, "$1 ").trim();

function Row({ label, value, testId }) {
  const copy = () => { navigator.clipboard?.writeText(value); toast.success(`${label} copiato`); };
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-mono text-sm text-slate-900 break-all" data-testid={testId}>{value}</div>
      </div>
      <button type="button" onClick={copy} className="p-1.5 rounded-md hover:bg-slate-100" aria-label={`Copia ${label}`}><Copy className="w-3.5 h-3.5 text-slate-500" /></button>
    </div>
  );
}

export function BankTransferInstructions({ orderId, amount, bank, onDeclared }) {
  const [busy, setBusy] = useState(false);
  if (!bank?.iban) {
    return <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="bank-missing-iban">Il fornitore non ha ancora inserito l'IBAN. Scegli un altro metodo o chiediglielo in chat.</div>;
  }
  const causale = `AA-${orderId.slice(-8).toUpperCase()}`;
  const declare = async () => {
    setBusy(true);
    try { await api.post(`/orders/${orderId}/bank-transfer/declare`); toast.success("Bonifico dichiarato · in attesa di conferma del fornitore"); onDeclared?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="bank-transfer-panel">
      <div className="rounded-xl border border-slate-200 p-3">
        <Row label="Beneficiario" value={bank.holder} testId="bank-holder" />
        <Row label="IBAN" value={fmtIban(bank.iban)} testId="bank-iban" />
        <Row label="Importo" value={`€ ${Number(amount).toFixed(2)}`} testId="bank-amount" />
        <Row label="Causale" value={causale} testId="bank-reference" />
      </div>
      <div className="text-[11px] text-slate-600">Esegui un <b>bonifico istantaneo</b> dalla tua banca con questi dati, poi premi il pulsante. L'ordine passa a "pagato" quando il fornitore conferma la ricezione.</div>
      <Button onClick={declare} disabled={busy} className="w-full ga-btn-primary rounded-full" data-testid="bank-declare-button">
        <Landmark className="w-4 h-4 mr-2" />{busy ? "Invio..." : "Ho inviato il bonifico"}
      </Button>
    </div>
  );
}

export function BankTransferStatus({ order, isWinner, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const p = order.payment_pending;
  if (!p || p.method !== "bank_transfer" || order.status !== "awarded") return null;
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/orders/${order.id}/bank-transfer/confirm`); toast.success("Ricezione bonifico confermata · ordine pagato"); onConfirmed?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm" data-testid="bank-pending-panel">
      <div className="text-xs font-mono uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><Landmark className="w-3 h-3" /> Bonifico dichiarato · in verifica</div>
      <div className="mt-1 text-xs text-amber-900">€ {Number(p.amount).toFixed(2)} · causale <b className="font-mono">{p.reference}</b> · {new Date(p.declared_at).toLocaleString("it-IT")}</div>
      {isWinner ? (
        <Button onClick={confirm} disabled={busy} className="mt-3 w-full ga-btn-primary rounded-full" data-testid="bank-confirm-button">
          <CheckCircle2 className="w-4 h-4 mr-2" />{busy ? "Conferma..." : "Ho ricevuto il bonifico"}
        </Button>
      ) : (
        <div className="mt-2 text-xs text-amber-800">In attesa che il fornitore confermi la ricezione sul suo conto.</div>
      )}
    </div>
  );
}
